/**
 * Chart tile config (S6) — same field idioms as the chart builder
 * (type/x/y/series/legend/grid, YAxisEntryRow reused verbatim) plus the
 * reference-lines editor (value + label + severity → S3's ReferenceLine
 * rendering).
 */
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import type { AggregationFunction, ChartType } from '../../../charts/types/chartTypes';
import { CHART_TYPES, CHART_TYPE_LABELS } from '../../../charts/types/chartTypes';
import { getYAxisCandidates } from '../../../charts/utils/columnTypeDetection';
import { YAxisEntryRow } from '../../../charts/components/YAxisEntryRow';
import { FieldRow, ColumnSelect, EnumSelect, NO_COLUMNS_HINT, type SectionProps } from './configFields';
import type { RefLineRow } from './addTileFormModel';

const SINGLE_Y_TYPES: ChartType[] = ['pie', 'scatter'];
const MAX_Y = 4;

export function ChartConfigSection({ values, set, columnInfos }: SectionProps) {
  const numericColumns = getYAxisCandidates(columnInfos);
  const columnOptions = columnInfos.map((c) => ({ value: c.key, label: c.name }));
  const noColumns = columnInfos.length === 0;
  const singleY = SINGLE_Y_TYPES.includes(values.chartType);
  const yEntries = singleY ? values.chartY.slice(0, 1) : values.chartY;

  const setY = (next: Array<{ column: string; aggregation: AggregationFunction }>) =>
    set({ chartY: next });
  const setLines = (next: RefLineRow[]) => set({ referenceLines: next });

  return (
    <div className="flex flex-col gap-2" data-testid="add-tile-config-chart">
      <FieldRow label="Chart type">
        <EnumSelect
          value={values.chartType}
          onChange={(chartType) =>
            set(
              SINGLE_Y_TYPES.includes(chartType)
                ? { chartType, chartY: values.chartY.slice(0, 1) }
                : { chartType },
            )
          }
          options={CHART_TYPES.map((t) => ({ value: t, label: CHART_TYPE_LABELS[t] }))}
          testId="add-tile-chart-type"
        />
      </FieldRow>
      <FieldRow label="Category (X)">
        <ColumnSelect
          value={values.chartX}
          onChange={(chartX) => set({ chartX })}
          columnInfos={columnInfos}
          categories={['string', 'date', 'boolean']}
          testId="add-tile-chart-x"
        />
      </FieldRow>
      <div className="grid grid-cols-[92px_1fr] items-start gap-2">
        <Label className="pt-1.5 text-xs text-muted-foreground">Values (Y)</Label>
        <div className="flex flex-col gap-1.5" data-testid="add-tile-chart-y-0">
          {/* The Y rows reuse the chart builder's YAxisEntryRow, whose own
              "Select" placeholder can't explain WHY it has nothing to offer
              before a preview has run — say it once here instead. */}
          {noColumns && (
            <p className="text-[11px] text-muted-foreground" data-testid="add-tile-chart-y-empty">
              {NO_COLUMNS_HINT}.
            </p>
          )}
          {yEntries.map((entry, index) => (
            <YAxisEntryRow
              key={index}
              index={index}
              column={entry.column}
              aggregation={entry.aggregation}
              numericColumns={numericColumns}
              allColumnOptions={columnOptions}
              showRemove={yEntries.length > 1}
              onColumnChange={(column) =>
                setY(yEntries.map((e, i) => (i === index ? { ...e, column } : e)))
              }
              onAggregationChange={(aggregation) =>
                setY(yEntries.map((e, i) => (i === index ? { ...e, aggregation } : e)))
              }
              onRemove={() => setY(yEntries.filter((_, i) => i !== index))}
            />
          ))}
          {!singleY && yEntries.length < MAX_Y && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-fit px-1.5 text-xs"
              onClick={() => setY([...yEntries, { column: '', aggregation: 'sum' }])}
            >
              <Plus className="mr-1 h-3 w-3" /> Add value
            </Button>
          )}
        </div>
      </div>
      {values.chartType !== 'pie' && (
        <FieldRow label="Group by">
          <ColumnSelect
            value={values.chartSeries}
            onChange={(chartSeries) => set({ chartSeries })}
            columnInfos={columnInfos}
            categories={['string', 'boolean']}
            allowNone
            testId="add-tile-chart-series"
          />
        </FieldRow>
      )}
      <div className="flex items-center gap-5 pl-[100px]">
        <label className="flex cursor-pointer items-center gap-1.5 text-xs">
          <Switch
            checked={values.chartShowLegend}
            onCheckedChange={(chartShowLegend) => set({ chartShowLegend })}
          />
          Legend
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-xs">
          <Switch
            checked={values.chartShowGrid}
            onCheckedChange={(chartShowGrid) => set({ chartShowGrid })}
          />
          Grid
        </label>
      </div>
      <div className="grid grid-cols-[92px_1fr] items-start gap-2">
        <Label className="pt-1.5 text-xs text-muted-foreground">Reference lines</Label>
        <div className="flex flex-col gap-1.5" data-testid="add-tile-reference-lines">
          {values.referenceLines.map((line, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <Input
                type="number"
                className="h-7 w-24 text-xs"
                value={Number.isFinite(line.value) ? line.value : ''}
                placeholder="Value"
                onChange={(e) =>
                  setLines(
                    values.referenceLines.map((l, i) =>
                      i === index ? { ...l, value: e.target.valueAsNumber } : l,
                    ),
                  )
                }
              />
              <Input
                className="h-7 flex-1 text-xs"
                value={line.label}
                placeholder="Label (optional)"
                onChange={(e) =>
                  setLines(
                    values.referenceLines.map((l, i) =>
                      i === index ? { ...l, label: e.target.value } : l,
                    ),
                  )
                }
              />
              <EnumSelect
                value={line.severity}
                onChange={(severity) =>
                  setLines(
                    values.referenceLines.map((l, i) => (i === index ? { ...l, severity } : l)),
                  )
                }
                options={[
                  { value: 'none', label: 'Neutral' },
                  { value: 'warning', label: 'Warning' },
                  { value: 'critical', label: 'Critical' },
                ]}
              />
              <button
                type="button"
                className="rounded-sm p-1 text-muted-foreground hover:text-destructive"
                onClick={() => setLines(values.referenceLines.filter((_, i) => i !== index))}
                aria-label="Remove reference line"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-fit px-1.5 text-xs"
            onClick={() =>
              setLines([...values.referenceLines, { value: NaN, label: '', severity: 'none' }])
            }
            data-testid="add-tile-add-reference-line"
          >
            <Plus className="mr-1 h-3 w-3" /> Add line
          </Button>
        </div>
      </div>
    </div>
  );
}
